import * as Sentry from '@sentry/react'
import { UpdateSponsorRequestPayload } from 'app/api/sponsorRequest'
import { ButtonApiErrorWrapper } from 'app/components/shared/ButtonApiErrorWrapper'
import { BgShapes } from 'app/components/ui/BgShapes'
import { Button } from 'app/components/ui/Button'
import { Container } from 'app/components/ui/Container'
import { Form } from 'app/components/ui/Form'
import { LoadingSpinner } from 'app/components/ui/LoadingSpinner'
import { Text } from 'app/components/ui/Text'
import { useGetAllLocations } from 'app/queries/location'
import {
  useCreateSponsorRequest,
  useUpdateSponsorRequest,
} from 'app/queries/sponsorRequests'
import { protectedRoutes } from 'app/routing/routes'
import { ScrollToTop } from 'app/routing/ScrollToTop'
import { SponsorRequest, Location } from 'EstraSponsor'
import React, { useState } from 'react'
import { useErrorHandler } from 'react-error-boundary'
import { useHistory } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import {
  SponsorRequestFormData,
  sponsorRequestDefaultValues,
  adaptApiToFormData,
  sponsorRequestValidationSchema,
  adaptFormDataToApi,
} from './form'
import { SponsorRequestDataInnerForm } from './SponsorRequestDataInnerForm'
type Props = {
  sponsorRequest?: SponsorRequest
  onNextStep: (data: SponsorRequest) => void
}

export const SponsorRequestDataForm = ({
  sponsorRequest,
  onNextStep,
}: Props): JSX.Element => {
  const { push } = useHistory()
  const theme = useTheme()

  const {
    data: locations,
    error: getAllLocationsError,
    isLoading: getAllLocationsIsLoading,
  } = useGetAllLocations()

  const {
    mutate: createSponsorRequest,
    isLoading: createSponsorRequestLoading,
    error: createSponsorRequestError,
  } = useCreateSponsorRequest()

  const {
    mutate: updateSponsorRequest,
    isLoading: updateSponsorRequestLoading,
    error: updateSponsorRequestError,
  } = useUpdateSponsorRequest()

  const [sponsorRequestDraftId, setSponsorRequestDraftId] = useState<
    number | undefined
  >()

  const [isApiValidationError, setIsApiValidationError] = useState<
    boolean | undefined
  >()

  useErrorHandler(getAllLocationsError)

  if (getAllLocationsIsLoading) return <LoadingSpinner />

  const isLoading = createSponsorRequestLoading || updateSponsorRequestLoading
  const error = createSponsorRequestError || updateSponsorRequestError

  // This submit will be called even when the form is not valid (name has to be valid), with isDraft to true,
  // in this case the request will be saved as draft
  const onSubmit = async (
    data: SponsorRequestFormData,
    isFormValid?: boolean
  ) => {
    setIsApiValidationError(false)

    // If a sponsor request is not passed and a draft has not been saved, create a new sponsor request
    // otherwise is an update
    if (!sponsorRequest && !sponsorRequestDraftId) {
      createSponsorRequest(adaptFormDataToApi(data), {
        onSuccess: (createdSponsorRequest) => {
          // Save created sponsor request ID on state,
          // this is needed to edit it on the next save
          setSponsorRequestDraftId(createdSponsorRequest.id)

          // Go to next step only if all data is valid, otherwise is just a draft save
          if (
            !createdSponsorRequest.errors ||
            Object.keys(createdSponsorRequest.errors).length === 0
          ) {
            onNextStep(createdSponsorRequest)
          }
          // If form is valid but BE returned some errors, it means there is a misalignment between BE and FE, this should not happen
          else if (isFormValid) {
            Sentry.withScope((scope) => {
              scope.setExtra(
                'api_validation_errors',
                createdSponsorRequest.errors
              )
              scope.setLevel(Sentry.Severity.Error)
              Sentry.captureMessage(
                `Error during sponsor request creation/update, there is a misalignment between FE form validation and BE validation, the form is valid for the FE validation, but the BE returned some errors. See 'additional_data' for more info.`
              )
            })
            setIsApiValidationError(true)
          }
        },
      })
    } else {
      const apiData = adaptFormDataToApi(data) as UpdateSponsorRequestPayload

      updateSponsorRequest(
        {
          sponsor_request_id: (
            sponsorRequest?.id || sponsorRequestDraftId
          )?.toString() as string,
          ...apiData,
        },
        {
          onSuccess: (updatedSponsorRequest) => {
            // Go to next step only if all data is valid, otherwise is just a draft save
            if (
              !updatedSponsorRequest.errors ||
              Object.keys(updatedSponsorRequest.errors).length === 0
            ) {
              onNextStep(updatedSponsorRequest)
            }
            // If form is valid but BE returned some errors, it means there is a misalignment between BE and FE, this should not happen
            else if (isFormValid) {
              Sentry.withScope((scope) => {
                scope.setExtra(
                  'api_validation_errors',
                  updatedSponsorRequest.errors
                )
                scope.setLevel(Sentry.Severity.Error)
                Sentry.captureMessage(
                  `Error during sponsor request creation/update, there is a misalignment between FE form validation and BE validation, the form is valid for the FE validation, but the BE returned some errors. See 'additional_data' for more info.`
                )
              })
              setIsApiValidationError(true)
            }
          },
        }
      )
    }
  }

  return (
    <>
      <ScrollToTop />
      <Container>
        <HeaderWrapper>
          <Text variant='s20' mb={2}>
            In questo modulo è attiva una funzione di “bozza” che, utilizzando
            il pulsante “SALVA I DATI” in fondo alla pagina, ti consentirà di
            riprendere la compilazione in più momenti, a tuo comodo.
            {'\n'}
            {'\n'}L&apos;emissione della fattura da parte dello sponsee dovrà
            avvenire solo ad evento concluso o ad attività sportiva iniziata
            (nel caso di una sponsorizzazione di una stagione sportiva).
            Dovranno prima essere inviati anche i giustificativi completi della
            visibilità del logo definita nell&apos;accordo (foto, screenshot o
            materiali cartacei del logo nella vostra comunicazione e/o
            location). La fattura verrà saldata a 90 GG DFMF a meno di accordi
            differenti in merito.
          </Text>
        </HeaderWrapper>
      </Container>
      <Form<SponsorRequestFormData>
        // If sponsorRequest is present it means that we in edit, or that we went back from the second step (both update or create),
        // otherwise is a create
        defaultValues={
          sponsorRequest
            ? adaptApiToFormData(sponsorRequest)
            : sponsorRequestDefaultValues
        }
        validationSchema={sponsorRequestValidationSchema}
        onSubmit={(data) => onSubmit(data, true)}
        // Needed to save the sponsor request draft when when form is invalid
        onErrorSubmit={({ errors, data }) => {
          if (!errors['name']) {
            onSubmit(data)
          }
        }}
      >
        <BgShapes bgColor={theme.color.neutri.grigioBg} variant='form-section'>
          <FormWrapper>
            <SponsorRequestDataInnerForm
              locations={locations as Location[]}
              isMultiRegionaleOn={sponsorRequest?.region?.id === 21}
            ></SponsorRequestDataInnerForm>
          </FormWrapper>
        </BgShapes>

        <ActionContainer>
          <ButtonApiErrorWrapper
            isError={!!error || !!isApiValidationError}
            error={error}
            customMessage={
              isApiValidationError ? 'Si è verificato un Errore.' : undefined
            }
            alignCenter
          >
            <Text variant='s24'>
              A questo punto, se hai compilato TUTTI i campi, cliccando sul
              pulsante “SALVA I DATI” qui sotto, procederai alla pagina
              successiva, altrimenti salverai il modulo in bozza e potrai
              terminare la compilazione più tardi.
            </Text>
            <ButtonsWrapper>
              <Button
                size='lg'
                onClick={() => push(protectedRoutes.dashboard.path)}
                variant='grey'
              >
                Torna alla home
              </Button>
              <Button size='lg' type='submit' disabled={isLoading}>
                {isLoading ? 'Caricamento...' : 'Salva i dati'}
              </Button>
            </ButtonsWrapper>
          </ButtonApiErrorWrapper>
        </ActionContainer>
      </Form>
    </>
  )
}

const FormWrapper = styled.div`
  padding-top: 2rem;
`

const HeaderWrapper = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`

const ActionContainer = styled(Container)`
  margin-top: 2rem;
`

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;

  margin-top: 2rem;
`
